import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileCog, FileText, Loader2 } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { Pill } from './ui/Pill';
import { RailGenerateForm } from './PaymentRailForms';
import { useConfirm } from './ui/ConfirmDialog';
import { api } from '../lib/api';
import { formatAmount, formatDate } from '../lib/format';

type Rail = 'zengin' | 'swift' | 'ach' | 'sepa' | 'wise' | 'manual';

interface PaymentRun {
  id: string;
  status: 'draft' | 'scheduled' | 'executing' | 'complete' | 'failed';
  rail: Rail;
  base_currency: string;
  total_base_cents: string;
  scheduled_for: string | null;
  executed_at: string | null;
  zengin_file_url: string | null;
  swift_payload: Record<string, unknown> | null;
  created_at: string;
}

interface PaymentItem {
  id: string;
  ap_invoice_id: string | null;
  expense_report_id: string | null;
  vendor_id: string | null;
  amount_cents: string;
  currency: string;
  base_amount_cents: string;
  status: string;
}

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">
        {label}
        {hint && <span className="ml-1 normal-case tracking-normal text-ink-400">({hint})</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

/** Downloads the most-recently-generated rail file via blob fetch (cookie auth). */
async function downloadGeneratedFile(wsId: string, runId: string): Promise<void> {
  const r = await api.get(`/workspaces/${wsId}/payment-runs/${runId}/file`, { responseType: 'blob' });
  const cd = (r.headers as { 'content-disposition'?: string })['content-disposition'] ?? '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : `payment-run-${runId}.dat`;
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function PaymentRunDrawer({
  wsId,
  runId,
  open,
  onOpenChange,
}: {
  wsId: string;
  runId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ run: PaymentRun; items: PaymentItem[] }>({
    queryKey: ['payment-run', wsId, runId],
    enabled: !!wsId && !!runId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/payment-runs/${runId}`)).data,
  });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const setStatus = useMutation({
    mutationFn: async (status: string) => (await api.post(`/workspaces/${wsId}/payment-runs/${runId}/status/${status}`)).data,
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['payment-run', wsId, runId] });
      qc.invalidateQueries({ queryKey: ['payment-runs', wsId] });
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Status change failed'),
  });

  // 支払通知書: generate the PDF, then download it via the generic file route.
  const generateNotice = useMutation({
    mutationFn: async () => {
      await api.post(`/workspaces/${wsId}/payment-runs/${runId}/notice`);
      await downloadGeneratedFile(wsId, runId as string);
    },
    onSuccess: () => {
      setError(null);
      setInfo(t('payments.noticeGenerated'));
      qc.invalidateQueries({ queryKey: ['payment-run', wsId, runId] });
    },
    onError: (err: any) => {
      setInfo(null);
      setError(err?.response?.data?.message ?? err?.message ?? 'Notice generation failed');
    },
  });

  const run = data?.run;
  const items = data?.items ?? [];

  // Detect whether a file has been generated. zengin_file_url is the JP-only
  // column; the JSONB swift_payload covers swift/ach/sepa/wise via .blobKey.
  const hasFile =
    !!run?.zengin_file_url ||
    !!(run?.swift_payload && typeof run.swift_payload === 'object' && 'blobKey' in run.swift_payload);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={run ? `${t(`payments.rails.${run.rail}`)} · ${run.id.slice(0, 8)}` : '…'}
      description={run ? formatDate(run.scheduled_for ?? run.created_at, i18n.language) : undefined}
      footer={
        run ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Pill tone={
              run.status === 'complete' ? 'success' :
              run.status === 'failed' ? 'warn' :
              run.status === 'draft' ? 'muted' : 'accent'
            }>
              {t(`common.${run.status}`)}
            </Pill>
            <div className="flex items-center gap-2">
              {hasFile && (
                <button
                  type="button"
                  onClick={() => downloadGeneratedFile(wsId, run.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <Download size={12} />
                  {t('payments.downloadFile')}
                </button>
              )}
              <button
                type="button"
                onClick={() => generateNotice.mutate()}
                disabled={generateNotice.isPending || items.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
              >
                {generateNotice.isPending ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                {t('payments.paymentNotice')}
              </button>
              {run.rail !== 'manual' && (
                <button
                  type="button"
                  onClick={() => setShowForm((s) => !s)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <FileCog size={12} />
                  {hasFile ? t('payments.regenerateFile') : t('payments.generateFile')}
                </button>
              )}
              {(run.status === 'draft') && (
                <button type="button" onClick={() => setStatus.mutate('scheduled')} disabled={setStatus.isPending}
                  className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50">
                  {t('payments.schedule')}
                </button>
              )}
              {(run.status === 'draft' || run.status === 'scheduled') && (
                <button type="button" onClick={() => setStatus.mutate('complete')} disabled={setStatus.isPending}
                  className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
                  {t('payments.markComplete')}
                </button>
              )}
              {(run.status === 'draft' || run.status === 'scheduled') && (
                <button type="button" onClick={async () => { if (await confirm({ title: t('payments.confirmCancel'), confirmLabel: t('payments.cancel'), destructive: true })) setStatus.mutate('failed'); }} disabled={setStatus.isPending}
                  className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium text-warn-600 hover:bg-warn-50 dark:hover:bg-warn-900/20 disabled:opacity-50">
                  {t('payments.cancel')}
                </button>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {isLoading || !run ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="text-sm text-emerald-700 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
              {info}
            </div>
          )}

          <section className="grid grid-cols-3 gap-3 text-sm">
            <FormField label={t('payments.rail')}>{t(`payments.rails.${run.rail}`)}</FormField>
            <FormField label={t('payments.baseCurrency')}>{run.base_currency}</FormField>
            <FormField label={t('inbox.field.total')}>
              <span className="num font-semibold">
                {formatAmount(run.total_base_cents, run.base_currency, i18n.language)}
              </span>
            </FormField>
          </section>

          {showForm && run.rail === 'zengin' && (
            <ZenginGenerateForm
              wsId={wsId}
              runId={run.id}
              items={items}
              onGenerated={() => {
                setShowForm(false);
                setError(null);
                setInfo(t('payments.zenginGenerated'));
                qc.invalidateQueries({ queryKey: ['payment-run', wsId, run.id] });
                qc.invalidateQueries({ queryKey: ['payment-runs', wsId] });
              }}
              onError={(msg) => {
                setError(msg);
                setInfo(null);
              }}
            />
          )}

          {showForm && run.rail !== 'zengin' && run.rail !== 'manual' && (
            <RailGenerateForm
              rail={run.rail}
              wsId={wsId}
              runId={run.id}
              items={items}
              baseCurrency={run.base_currency}
              onGenerated={() => {
                setShowForm(false);
                setError(null);
                setInfo(t('payments.fileGenerated'));
                qc.invalidateQueries({ queryKey: ['payment-run', wsId, run.id] });
                qc.invalidateQueries({ queryKey: ['payment-runs', wsId] });
              }}
              onError={(msg) => {
                setError(msg);
                setInfo(null);
              }}
            />
          )}

          <section>
            <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
              {t('payments.items')} ({items.length})
            </h3>
            {items.length === 0 ? (
              <div className="text-sm text-ink-500 py-6 text-center rounded-lg border border-dashed border-ink-200 dark:border-ink-800">
                {t('payments.noItems')}
              </div>
            ) : (
              <ul className="divide-y divide-ink-200 dark:divide-ink-800 rounded-lg border border-ink-200 dark:border-ink-800">
                {items.map((it) => (
                  <li key={it.id} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div className="font-mono text-xs text-ink-400">
                      {(it.ap_invoice_id ?? it.expense_report_id ?? it.id).slice(0, 8)}
                    </div>
                    <span className="num">{formatAmount(it.amount_cents, it.currency, i18n.language)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** JP-priority Zengin file generator. Hand-fills sender bank info; recipients
 *  default to one row per payment item but the operator can edit/add. */
function ZenginGenerateForm({
  wsId,
  runId,
  items,
  onGenerated,
  onError,
}: {
  wsId: string;
  runId: string;
  items: PaymentItem[];
  onGenerated: () => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();

  // Sender fields — bank account the funds leave from.
  const [consignorCode, setConsignorCode] = useState('');
  const [consignorName, setConsignorName] = useState('');
  const [transferDate, setTransferDate] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}${dd}`;
  });
  const [senderBankCode, setSenderBankCode] = useState('');
  const [senderBankName, setSenderBankName] = useState('');
  const [senderBranchCode, setSenderBranchCode] = useState('');
  const [senderBranchName, setSenderBranchName] = useState('');
  const [senderAccountKind, setSenderAccountKind] = useState<'1' | '2' | '4' | '9'>('1');
  const [senderAccountNumber, setSenderAccountNumber] = useState('');

  interface ZRow {
    bankCode: string;
    bankName: string;
    branchCode: string;
    branchName: string;
    accountKind: '1' | '2' | '4';
    accountNumber: string;
    receiverName: string;
    amountYen: number;
  }

  const [rows, setRows] = useState<ZRow[]>(
    items.map((it) => ({
      bankCode: '',
      bankName: '',
      branchCode: '',
      branchName: '',
      accountKind: '1' as const,
      accountNumber: '',
      receiverName: '',
      amountYen: Number(it.amount_cents),
    })),
  );

  const generate = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/workspaces/${wsId}/payment-runs/${runId}/zengin`, {
          sender: {
            consignorCode,
            consignorName,
            transferDateMMDD: transferDate,
            senderBankCode,
            senderBankName,
            senderBranchCode,
            senderBranchName,
            senderAccountKind,
            senderAccountNumber,
          },
          recipients: rows,
        })
      ).data,
    onSuccess: () => onGenerated(),
    onError: (err: any) => onError(err?.response?.data?.message ?? err?.message ?? 'Generate failed'),
  });

  return (
    <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 bg-ink-50 dark:bg-ink-900/50 space-y-4">
      <div>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-1">{t('payments.zenginSender')}</h3>
        <p className="text-[11px] text-ink-500 mb-3">{t('payments.zenginSenderHint')}</p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('payments.consignorCode')} hint="10 digits">
            <input
              value={consignorCode}
              onChange={(e) => setConsignorCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className={inputCn}
            />
          </FormField>
          <div className="col-span-2">
            <FormField label={t('payments.consignorName')} hint="ｶﾅ 40">
              <input
                value={consignorName}
                onChange={(e) => setConsignorName(e.target.value.slice(0, 40))}
                placeholder="ｶ)ｷﾗﾘ"
                className={inputCn}
              />
            </FormField>
          </div>
          <FormField label={t('payments.transferDate')} hint="MMDD">
            <input
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className={inputCn}
            />
          </FormField>
          <FormField label={t('payments.senderBank')} hint="4">
            <input
              value={senderBankCode}
              onChange={(e) => setSenderBankCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0001"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('payments.senderBankName')} hint="ｶﾅ 15">
            <input
              value={senderBankName}
              onChange={(e) => setSenderBankName(e.target.value.slice(0, 15))}
              placeholder="ﾐｽﾞﾎ"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('payments.senderBranch')} hint="3">
            <input
              value={senderBranchCode}
              onChange={(e) => setSenderBranchCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="001"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('payments.senderBranchName')} hint="ｶﾅ 15">
            <input
              value={senderBranchName}
              onChange={(e) => setSenderBranchName(e.target.value.slice(0, 15))}
              placeholder="ﾎﾝﾃﾝ"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('payments.senderAccountKind')}>
            <select
              value={senderAccountKind}
              onChange={(e) => setSenderAccountKind(e.target.value as '1' | '2' | '4' | '9')}
              className={inputCn}
            >
              <option value="1">1 · 普通</option>
              <option value="2">2 · 当座</option>
              <option value="4">4 · 貯蓄</option>
              <option value="9">9 · その他</option>
            </select>
          </FormField>
          <FormField label={t('payments.senderAccountNumber')} hint="7">
            <input
              value={senderAccountNumber}
              onChange={(e) => setSenderAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 7))}
              className={inputCn}
            />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('payments.zenginRecipients')} ({rows.length})
        </h3>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900">
              <input
                value={r.bankCode}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, bankCode: e.target.value.replace(/\D/g, '').slice(0, 4) } : rr)))
                }
                placeholder={t('payments.zenginCol.bank')}
                className={`${inputCn} col-span-1`}
              />
              <input
                value={r.branchCode}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, branchCode: e.target.value.replace(/\D/g, '').slice(0, 3) } : rr)))
                }
                placeholder={t('payments.zenginCol.branch')}
                className={`${inputCn} col-span-1`}
              />
              <select
                value={r.accountKind}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, accountKind: e.target.value as '1' | '2' | '4' } : rr)))
                }
                className={`${inputCn} col-span-1`}
              >
                <option value="1">普通</option>
                <option value="2">当座</option>
                <option value="4">貯蓄</option>
              </select>
              <input
                value={r.accountNumber}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 7) } : rr)))
                }
                placeholder={t('payments.zenginCol.account')}
                className={`${inputCn} col-span-2`}
              />
              <input
                value={r.receiverName}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, receiverName: e.target.value.slice(0, 30) } : rr)))
                }
                placeholder="ｶ)ｱｲｳｴｵ"
                className={`${inputCn} col-span-5`}
              />
              <input
                type="number"
                value={r.amountYen}
                onChange={(e) =>
                  setRows(rows.map((rr, idx) => (idx === i ? { ...rr, amountYen: Number(e.target.value) || 0 } : rr)))
                }
                placeholder={t('payments.zenginCol.amount')}
                className={`${inputCn} col-span-2 text-right`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
        >
          {t('payments.generateZenginFile')}
        </button>
      </div>
    </section>
  );
}
